import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Trash2, ExternalLink, Search, ListOrdered } from "lucide-react";
import { api, errText } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import { catLabel } from "../../lib/i18n";
import { CATEGORY_LABEL } from "../../lib/constants";
import { kindLabel } from "../../lib/litters";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { FrequencyPicker } from "../SaveSearchButton";
import { Empty, Loader } from "./shared";
import { MyFollowing } from "./MyFollowing";
import { fmtSlot } from "../../lib/slots";

const browseLink = (f) => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => p.set(k === "lf_type" ? "lf" : k, String(v)));
  return `/browse?${p.toString()}`;
};

const summarize = (f, t, lang) => {
  const parts = [];
  if (f.q) parts.push(`“${f.q}”`);
  if (f.category) parts.push(catLabel(lang, f.category, CATEGORY_LABEL[f.category] || f.category));
  if (f.lf_type) parts.push(t(`lmap_${f.lf_type}`));
  if (f.city || f.province) parts.push([f.city, f.province].filter(Boolean).join(", "));
  const lo = f.min_price, hi = f.max_price;
  if (lo != null && hi != null) parts.push(`$${lo}–$${hi}`);
  else if (lo != null) parts.push(`${t("ss_from")} $${lo}`);
  else if (hi != null) parts.push(`${t("ss_upto")} $${hi}`);
  if (f.verified) parts.push(t(`ss_verified_${f.verified}`));
  return parts.join(" · ") || t("ss_all");
};

const SavedRow = ({ s, onChange, onDelete }) => {
  const { t, lang } = useLanguage();
  const summary = summarize(s.filters, t, lang);
  const setFreq = async (frequency) => {
    try { const { data } = await api.patch(`/saved-searches/${s.id}`, { frequency }); onChange(data); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-4" data-testid={`saved-search-${s.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-semibold text-[#12234A] truncate">{s.name === s.summary ? summary : s.name}</p>
          <p className="text-xs text-slate-500 mt-0.5">{summary}</p>
          <p className="text-[11px] text-slate-400 mt-1">
            {s.sent_count} {t("ss_sent")}{s.frequency === "daily" && s.pending_count > 0 ? ` · ${s.pending_count} ${t("ss_pending")}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link to={browseLink(s.filters)} data-testid={`saved-search-view-${s.id}`} className="text-xs font-semibold text-[#F5551E] hover:underline inline-flex items-center gap-1"><ExternalLink size={13} /> {t("ss_view")}</Link>
          <AlertDialog>
            <AlertDialogTrigger asChild><button data-testid={`saved-search-delete-${s.id}`} aria-label={t("ss_delete")} className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={15} /></button></AlertDialogTrigger>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader><AlertDialogTitle>{t("lt_confirm_title")}</AlertDialogTitle><AlertDialogDescription>{t("ss_delete_q")}</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>{t("lt_cancel")}</AlertDialogCancel><AlertDialogAction onClick={() => onDelete(s.id)} data-testid={`saved-search-delete-confirm-${s.id}`} className="bg-red-500 hover:bg-red-600">{t("ss_delete")}</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <div className="mt-3 max-w-md"><FrequencyPicker value={s.frequency} onChange={setFreq} testid={`saved-search-freq-${s.id}`} /></div>
    </div>
  );
};

const MyWaitlists = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState(null);
  const [bookings, setBookings] = useState([]);
  const load = () => {
    api.get("/my/waitlists").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/my/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const leave = async (id) => {
    try { await api.delete(`/litters/${id}/waitlist`); load(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <section className="mt-10" data-testid="my-waitlists">
      <h3 className="font-display text-lg font-bold text-[#12234A] flex items-center gap-2 mb-3"><ListOrdered size={18} className="text-[#F5551E]" /> {t("ss_wl_title")}</h3>
      {items === null ? <Loader /> : items.length === 0 ? (
        <p className="text-sm text-slate-400">{t("ss_wl_empty")}</p>
      ) : (
        <div className="space-y-2">
          {items.map((w) => (
            <div key={w.litter_id} className="bg-white rounded-xl border border-slate-200/80 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`my-waitlist-${w.litter_id}`}>
              <span className="h-9 px-3 rounded-lg bg-purple-50 text-purple-700 font-bold text-sm inline-flex items-center">{t("ss_wl_pos")}{w.position}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#12234A] truncate">{w.title || `${w.breed} · ${kindLabel(w.kind, t)}`}</p>
                <Link to={`/seller/${w.breeder_id}`} className="text-xs text-slate-500 hover:text-[#F5551E]">{w.breeder_name}</Link>
                {w.contacted && <span className="ml-2 text-[10px] font-bold uppercase text-emerald-600">{t("lt_contacted")}</span>}
                {bookings.filter((b) => b.litter_id === w.litter_id).map((b) => (
                  <p key={b.id} className="mt-1 text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-2 py-1 inline-block mr-1" data-testid={`my-booking-${b.id}`}>
                    <strong>{t("sl_yours")}:</strong> {t(`sl_${b.kind}`)} · {fmtSlot(b.start, lang)}
                  </p>
                ))}
                {w.went_home && (
                  <Link to={`/seller/${w.breeder_id}#reviews`} data-testid={`my-waitlist-review-${w.litter_id}`} className="mt-1 text-[11px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-2 py-1 inline-block hover:bg-purple-100">
                    {t("rv_gone_note")} → {t("rv_leave")}
                  </Link>
                )}
                {w.reserved && (
                  <p className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 inline-block" data-testid={`my-waitlist-reserved-${w.litter_id}`}>
                    <strong>{t("wr_your_spot")}</strong>{w.deposit_note ? ` · ${w.deposit_note}` : ""}
                  </p>
                )}
              </div>
              <button onClick={() => leave(w.litter_id)} data-testid={`my-waitlist-leave-${w.litter_id}`} className="text-xs font-semibold text-slate-500 hover:text-red-600">{t("ss_wl_leave")}</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export function SavedSearches({ navigate }) {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/saved-searches").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const onChange = (s) => setItems((l) => l.map((x) => (x.id === s.id ? s : x)));
  const onDelete = async (id) => {
    try { await api.delete(`/saved-searches/${id}`); setItems((l) => l.filter((x) => x.id !== id)); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div data-testid="saved-searches-tab">
      <h3 className="font-display text-lg font-bold text-[#12234A] flex items-center gap-2 mb-3"><BellRing size={18} className="text-[#F5551E]" /> {t("ss_tab")}</h3>
      {items === null ? <Loader /> : items.length === 0 ? (
        <Empty icon={Search} text={t("ss_empty")} cta={t("ss_browse")} onCta={() => navigate("/browse")} />
      ) : (
        <div className="space-y-3">{items.map((s) => <SavedRow key={s.id} s={s} onChange={onChange} onDelete={onDelete} />)}</div>
      )}
      <MyFollowing />
      <MyWaitlists />
    </div>
  );
}
